import React, { useState, useEffect } from "react";
import { useIsMobile } from "../shared/utils";
import { bm, clearSession, getToken, setAuthLostHandler } from "./bmClient";
import { BMLogin } from "./BMLogin";
import { CampaignBoard } from "../portal/CampaignBoard";
import { BMSites } from "./BMSites";
import { BMSiteProfile } from "./BMSiteProfile";
import { BMTenantModal } from "./BMTenantModal";
import { bmColors, content, ghostButton, page } from "./bmStyles";

// The whole manager experience. Mounted by index.js instead of <App/> when the
// path starts with /bmportal, so the staff shell — sidebar, nav, every internal
// component — is never constructed at all. That is the containment: not hidden
// by a role check, simply absent.
export function ManagerPortal() {
  const isMobile = useIsMobile();

  const [profile, setProfile] = useState(null);
  const [checking, setChecking] = useState(!!getToken());
  // Three screens deep, each reached by choosing something on the one before:
  // the welcome list of buildings, a building's profile, and one of the things
  // open on it. A manager with two properties always knows which is on screen.
  const [site, setSite] = useState(null);
  const [screen, setScreen] = useState("profile");
  const [campaign, setCampaign] = useState(null);
  // The booking board brings its own scrolling layout, so it replaces the
  // padded content area rather than sitting inside it.
  const onBoard = !!site && screen === "bookings" && !!campaign;

  // A refresh that fails drops the session; re-render as signed out rather
  // than leaving a dead screen behind.
  useEffect(() => {
    setAuthLostHandler(() => setProfile(null));
  }, []);

  // Resume an existing session on reload.
  useEffect(() => {
    if (!getToken()) return;
    bm.get("/me/")
      .then((r) => {
        setProfile(r.data);
        setChecking(false);
      })
      .catch(() => {
        clearSession();
        setChecking(false);
      });
  }, []);

  const signOut = () => {
    clearSession();
    setProfile(null);
    setSite(null);
    setCampaign(null);
    window.location.assign("/bmportal");
  };

  if (checking)
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bmColors.darkGrey,
          color: "white",
        }}
      >
        Loading...
      </div>
    );

  if (!profile) return <BMLogin onSignedIn={setProfile} />;

  const sites = profile.sites || [];

  const openSite = (picked) => {
    setSite(picked);
    setScreen("profile");
  };

  const backToSites = () => {
    setSite(null);
    setCampaign(null);
  };

  // A photo the manager just uploaded. Written back into the profile as well
  // as the open building, so it is still there after going back to the list
  // and reopening — the list is what the building screens are built from.
  const photoUploaded = (updated) => {
    setSite(updated);
    setProfile((p) => ({
      ...p,
      sites: (p.sites || []).map((s) => (s.id === updated.id ? updated : s)),
    }));
  };

  // Every screen below the profile returns to it, never past it.
  const backToProfile = () => {
    setScreen("profile");
    setCampaign(null);
  };

  // The same board the office works, through /api/bm/ instead of /portal/api/.
  // The two flags turn off the controls a manager has no route for: there is
  // no DELETE on /api/bm/bookings/ and no /api/bm/slots/ at all.
  const board = (
    <CampaignBoard
      client={bm}
      campaignId={campaign?.id}
      onBack={backToProfile}
      backTitle={site?.site_name}
      canClearBooking={false}
      canBlockSlots={false}
      TenantModalComponent={BMTenantModal}
      fill
    />
  );

  return (
    <div style={page}>
      <div
        style={{
          backgroundColor: bmColors.darkGrey,
          color: bmColors.white,
          padding: isMobile ? "12px 16px" : "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? "16px" : "18px", fontWeight: "700" }}>
            Building Manager Portal
          </div>
        </div>
        <button
          onClick={signOut}
          style={{
            ...ghostButton,
            borderColor: "rgba(255,255,255,0.3)",
            color: bmColors.white,
            whiteSpace: "nowrap",
          }}
        >
          Sign out
        </button>
      </div>

      {onBoard ? (
        board
      ) : (
        <div style={content(isMobile)}>
          {!site ? (
            <BMSites
              sites={sites}
              isMobile={isMobile}
              onPick={openSite}
            />
          ) : (
            <BMSiteProfile
              site={site}
              isMobile={isMobile}
              onBack={backToSites}
              onPhotoUploaded={photoUploaded}
              onOpenCampaign={(c) => {
                setCampaign(c);
                setScreen("bookings");
              }}
            />
          )}
        </div>
      )}

    </div>
  );
}
